// import { toNumber } from '@freelancer/datastore/core';
import { Country } from '@freelancer/datastore/countries';
import { _transformCurrency } from '@freelancer/datastore/currencies';
import { CorporateDetails } from '@freelancer/datastore/profile-view-users';
import { Qualification } from '@freelancer/datastore/project-view-projects';
import { transformSkill } from '@freelancer/datastore/skills';
import {
  _transformUserImage,
  _transformUserStatus,
} from '@freelancer/datastore/users';
import {
  CountryApi,
  LocationApi,
  QualificationApi,
} from 'api-typings/common/common';
import {
  BadgeApi,
  CorporateDetailsApi,
  MembershipPackageApi,
  ReputationApi,
  ReputationDataApi,
  UserApi,
} from 'api-typings/users/users';
import { Badge } from './badge.model';
import { Location } from './location.model';
import {
  MembershipPackage,
  ProjectViewUser,
  UserResponsiveness,
} from './project-view-users.model';
import { Reputation, ReputationData } from './reputation.model';

// FIXME: optionals are a mess, 'private' _transformer imports which we use
// here to reuse logic, work out appropriate default values, eliminate throws
// as much as possible
export function transformProjectViewUsers(user: UserApi): ProjectViewUser {
  if (
    !user.avatar_cdn ||
    !user.avatar_large_cdn ||
    !user.chosen_role ||
    !user.id ||
    !user.location ||
    !user.primary_currency ||
    !user.registration_date ||
    !user.reputation ||
    !user.role ||
    !user.status ||
    !user.username
  ) {
    throw new ReferenceError(`Missing a required user field.`);
  }

  return {
    avatar: _transformUserImage(user.avatar_cdn),
    avatarLarge: _transformUserImage(user.avatar_large_cdn),
    badges: (user.badges || []).map(transformBadge),
    displayName: user.public_name || user.display_name || user.username,
    chosenRole: user.chosen_role,
    employerReputation: user.employer_reputation
      ? transformReputation(user.employer_reputation)
      : undefined,
    hourlyRate: user.hourly_rate,
    id: user.id,
    isNewFreelancer: user.reputation
      ? transformIsNewFreelancer(user.reputation)
      : undefined,
    jobs: (user.jobs || []).map(transformSkill),
    location: transformLocation(user.location),
    membershipPackage: user.membership_package
      ? transformMembership(user.membership_package)
      : undefined,
    preferredFreelancer: user.preferred_freelancer || false,
    primaryCurrency: _transformCurrency(user.primary_currency),
    primaryLanguage: user.primary_language,
    profileUrl: `/u/${user.username}`,
    qualifications: (user.qualifications || []).map(transformQualification),
    registrationDate: user.registration_date * 1000,
    reputation: transformReputation(user.reputation),
    responsiveness: transformResponsiveness(user.responsiveness),
    role: user.role,
    status: _transformUserStatus(user.status),
    tagLine: user.tagline || '',
    username: user.username,
    enterpriseIds: user.enterprise_ids,
    poolIds: user.pool_ids,
    escrowComInteractionRequired: !!user.escrowcom_interaction_required,
    corporate: transformCorporateDetailsApi(user.corporate),
  };
}

export function transformBadge(badge: BadgeApi): Badge {
  if (
    !badge.id ||
    !badge.name ||
    !badge.description ||
    !badge.time_awarded ||
    !badge.icon_url
  ) {
    throw new ReferenceError(`Missing a required timezone field.`);
  }

  return {
    id: badge.id,
    name: badge.name,
    description: badge.description,
    timeAwarded: badge.time_awarded * 1000,
    iconUrl: badge.icon_url,
  };
}

export function transformQualification(
  qualification: QualificationApi,
): Qualification {
  if (!qualification.icon_url) {
    throw new ReferenceError(`Missing an icon_url field.`);
  }

  return {
    id: qualification.id,
    name: qualification.name,
    level: qualification.level,
    type: qualification.type,
    iconUrl: qualification.icon_url.replace('/img/insignia/', ''),
    description: qualification.description,
    iconName: qualification.icon_name,
    scorePercentage: qualification.score_percentage,
    userPercentile: qualification.user_percentile,
  };
}

// NOTE: Readd this and update the model if timezone is needed
// export function transformTimezone(timezone: TimezoneApi): Timezone {
//   if (
//     !timezone.id ||
//     !timezone.country ||
//     !timezone.timezone ||
//     !timezone.offset
//   ) {
//     throw new ReferenceError(`Missing a required timezone field.`);
//   }

//   return {
//     id: timezone.id,
//     country: timezone.country,
//     timezone: timezone.timezone,
//     offset: timezone.offset,
//   };
// }

export function transformLocation(location: LocationApi): Location {
  if (!location.country) {
    throw new ReferenceError(`Missing a required location field.`);
  }

  return {
    country: transformCountry(location.country),
    city: location.city,
    mapCoordinates:
      location.latitude !== undefined && location.longitude !== undefined
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
          }
        : undefined,
    vicinity: location.vicinity,
    administrativeArea: location.administrative_area,
    fullAddress: location.full_address,
  };
}

export function transformLocationToLocationApi(
  location: Location,
): LocationApi {
  if (!location.country) {
    throw new ReferenceError(`Missing a required location field.`);
  }

  return {
    full_address: location.fullAddress,
    vicinity: location.vicinity,
    country: location.country,
    longitude: location.mapCoordinates
      ? location.mapCoordinates.longitude
      : undefined,
    latitude: location.mapCoordinates
      ? location.mapCoordinates.latitude
      : undefined,
    administrative_area: location.administrativeArea,
    city: location.city,
  };
}

// the countries returned from the Users API can be a little wonky sometimes
// this function is a bit more "safe" than the one in countries.transformers
export function transformCountry(country: CountryApi): Country {
  return {
    id: country.code || '',
    code: country.code || '',
    name: country.name || '',
    flagUrl: country.flag_url,
  };
}

export function transformReputation(reputation: ReputationApi): Reputation {
  return {
    entireHistory: transformReputationData(reputation.entire_history),
    last3Months: transformReputationData(reputation.last3months),
    last12Months: transformReputationData(reputation.last12months),
    userId: reputation.user_id,
    role: reputation.role,
    earningsScore: reputation.earnings_score,
    jobHistory: reputation.job_history,
    projectStats: reputation.project_stats,
  };
}

export function transformReputationData(
  repData: ReputationDataApi,
): ReputationData {
  return {
    overall: repData.overall,
    onBudget: repData.on_budget,
    // tslint:disable-next-line:validate-millisecond-timestamps
    onTime: repData.on_time,
    positive: repData.positive,
    all: repData.all,
    reviews: repData.reviews,
    incompleteReviews: repData.incomplete_reviews,
    complete: repData.complete,
    incomplete: repData.incomplete,
    earnings: repData.earnings,
    completionRate: Math.round(repData.completion_rate * 10 ** 2),
    rehireRate: repData.rehire_rate,
    categoryRatings: repData.category_ratings,
  };
}

export function transformMembership(
  membership: MembershipPackageApi,
): MembershipPackage {
  if (!membership.name) {
    throw Error(`Membership Package Object does not have name attribute.`);
  }
  if (!membership.id) {
    throw Error(`Membership Package Object does not have id attribute.`);
  }

  return {
    bidLimit: membership.bid_limit,
    bidRefreshRate: membership.bid_refresh_rate,
    id: membership.id,
    jobChangeLimit: membership.job_change_limit,
    jobLimit: membership.job_limit,
    name: membership.name,
    servicePostingLimit: membership.service_posting_limit,
    timeBidRefreshed: membership.time_bid_refreshed
      ? membership.time_bid_refreshed * 1000
      : undefined,
  };
}

function transformIsNewFreelancer(reputation: ReputationApi): boolean {
  return (
    !reputation.entire_history.reviews &&
    !reputation.earnings_score &&
    !reputation.entire_history.all
  );
}

export function transformResponsiveness(
  responsiveness: number | undefined,
): UserResponsiveness {
  switch (responsiveness) {
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
      return responsiveness;
    default:
      return undefined;
  }
}

export function transformCorporateDetailsApi(
  corporateDetails?: CorporateDetailsApi,
): CorporateDetails | undefined {
  if (!corporateDetails) {
    return undefined;
  }

  if (!corporateDetails.status || !corporateDetails.founder_id) {
    throw new Error(
      'Expecting corporate account status and founder id to be set.',
    );
  }

  return {
    status: corporateDetails.status,
    founderId: corporateDetails.founder_id,
  };
}
